import { css } from 'aphrodite';
import * as React from 'react';
import { AIMarkdownResponse } from '../../../../../../web/components-v2/ai/AiMarkdownResponse';
import { IndividualAnalysis } from '../../hooks/types';
import { styleSheet } from './styles';

interface CollapsibleSectionProps {
	title: string;
	children?: React.ReactNode;
	defaultExpanded?: boolean;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, defaultExpanded = false }) => {
	const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)} onClick={() => setIsExpanded(!isExpanded)}>
				<h4 className={css(styleSheet.headerTitle)}>{title}</h4>
				<svg
					className={css(styleSheet.chevron, isExpanded && styleSheet.chevronExpanded)}
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2'
				>
					<path d='M6 9l6 6 6-6' />
				</svg>
			</div>
			<div className={css(styleSheet.content, isExpanded && styleSheet.contentExpanded)}>
				<div className={css(styleSheet.contentInner)}>{children}</div>
			</div>
		</div>
	);
};

interface CollapsibleAnalysisItemProps {
	analysis: IndividualAnalysis;
	index: number;
	defaultExpanded?: boolean;
}

export const CollapsibleAnalysisItem: React.FC<CollapsibleAnalysisItemProps> = ({
	analysis,
	index,
	defaultExpanded = false,
}) => {
	const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

	const getStatusBadgeStyle = () => {
		switch (analysis.status) {
			case 'complete':
				return styleSheet.statusComplete;
			case 'error':
				return styleSheet.statusError;
			case 'analyzing':
				return styleSheet.statusAnalyzing;
			default:
				return null;
		}
	};

	const getStatusLabel = () => {
		switch (analysis.status) {
			case 'complete':
				return 'Complete';
			case 'error':
				return 'Error';
			case 'analyzing':
				return 'Analyzing...';
			case 'pending':
				return 'Pending';
			default:
				return '';
		}
	};

	return (
		<div className={css(styleSheet.item)}>
			<div className={css(styleSheet.itemHeader)} onClick={() => setIsExpanded(!isExpanded)}>
				<div className={css(styleSheet.itemHeaderContent)}>
					<h5 className={css(styleSheet.itemTitle)}>
						Demo {index + 1}: {analysis.demoName || 'Unknown Demo'}
					</h5>
					{analysis.demoDate && <p className={css(styleSheet.itemSubtitle)}>{analysis.demoDate}</p>}
				</div>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					{analysis.status !== 'pending' && (
						<span className={css(styleSheet.itemStatusBadge, getStatusBadgeStyle())}>{getStatusLabel()}</span>
					)}
					<svg
						className={css(styleSheet.chevron, isExpanded && styleSheet.chevronExpanded)}
						viewBox='0 0 24 24'
						fill='none'
						stroke='currentColor'
						strokeWidth='2'
					>
						<path d='M6 9l6 6 6-6' />
					</svg>
				</div>
			</div>
			{isExpanded && (
				<div className={css(styleSheet.itemContent)}>
					{analysis.status === 'error' ? (
						<p className={css(styleSheet.errorText)}>Error: {analysis.error || 'Failed to analyze this demo'}</p>
					) : analysis.status === 'complete' && analysis.analysis ? (
						<AIMarkdownResponse>{analysis.analysis}</AIMarkdownResponse>
					) : (
						<p>No analysis available yet.</p>
					)}
				</div>
			)}
		</div>
	);
};
